import { forwardRef, Fragment, HTMLAttributes, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { cssX, Dropdown, FCX, Portal, TextInput, useUuid } from "@datassential/platform-ui-lib";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import cn from "classnames";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";
import CustomScrollbarsVirtualList from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";
import useCategoryProfiles from "@apiHooks/useCategoryProfiles";
import useComponentVisible from "@hooks/useComponentVisible";
import { routeNames } from "@core/routeNames";
import { filterProfiles, getSortedProfiles, NamedCategoryProfile } from "./CategoryProfileSwitcher.helpers";
import { ITEM_SIZE, MAX_VISIBLE_ROWS } from "./CategoryProfileSwitcher.constants";
import "./CategoryProfileSwitcher.styles.scss";

interface Props {
    currentId: string;
    currentName: string;
}

// Highlights the first case-insensitive match as <b> using React nodes. Unlike the lib's
// markMatch, it neither builds a RegExp from user input nor injects unescaped HTML.
function renderHighlighted(name: string, query: string): ReactNode {
    if (!query) return name;
    const start = name.toLowerCase().indexOf(query.toLowerCase());
    if (start === -1) return name;
    const end = start + query.length;
    return (
        <Fragment>
            {name.slice(0, start)}
            <b>{name.slice(start, end)}</b>
            {name.slice(end)}
        </Fragment>
    );
}

interface ItemData {
    options: NamedCategoryProfile[];
    currentId: string;
    query: string;
    onSelect: (id: number) => void;
}

// react-window's inner element carries the grouping semantics so the rows stay its descendants.
const GroupInner = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ children, style }, ref) => (
    <div ref={ref} style={style} role="group" aria-label="Category profiles" className={cssX('Dropdown__group')}>
        {children}
    </div>
));
GroupInner.displayName = 'GroupInner';

function Option({ index, style, data }: ListChildComponentProps<ItemData>) {
    const { options, currentId, query, onSelect } = data;
    const profile = options[index];
    const isCurrent = String(profile.id) === currentId;
    return (
        <button
            type="button"
            aria-current={isCurrent || undefined}
            className={cn(cssX('Dropdown__item'), isCurrent && 'active-accessible')}
            style={style}
            onClick={() => onSelect(profile.id)}
        >
            <span>{renderHighlighted(profile.name, query)}</span>
        </button>
    );
}

const CategoryProfileSwitcher: FCX<Props> = ({ currentId, currentName, className, style, testId }) => {
    const history = useHistory();
    const dropdownId = useUuid();
    const panelRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const wasVisibleRef = useRef(false);
    const listRef = useRef<List<ItemData>>(null);
    const panelId = `${dropdownId}-panel`;
    // Dropdown renders in a Portal (outside `ref` in the DOM), so the panel is registered as
    // a second "inside" element; otherwise clicking the search input / scrollbar closes it.
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false, panelRef);
    const [search, setSearch] = useState('');
    const { profiles, isLoading, isError, reFetch } = useCategoryProfiles();

    const sorted = useMemo(() => getSortedProfiles(profiles), [profiles]);
    const filtered = useMemo(() => filterProfiles(sorted, search), [sorted, search]);
    const query = search.trim();

    useEffect(() => {
        if (!isComponentVisible) setSearch('');
    }, [isComponentVisible]);

    // Return focus to the trigger when the panel closes while focus was inside it (Escape,
    // selection). Outside clicks move focus elsewhere on purpose, so they are left alone.
    useEffect(() => {
        const justClosed = wasVisibleRef.current && !isComponentVisible;
        wasVisibleRef.current = isComponentVisible;
        if (!justClosed) return;
        const active = document.activeElement;
        const focusInPanel = !!active && !!panelRef.current && panelRef.current.contains(active);
        if (focusInPanel || active === document.body) triggerRef.current?.focus();
    }, [isComponentVisible]);

    const currentIndex = useMemo(
        () => filtered.findIndex(profile => String(profile.id) === currentId),
        [filtered, currentId],
    );

    // Bring the current profile into view when the list is shown (not while filtering).
    useEffect(() => {
        if (!isComponentVisible || isLoading || query || currentIndex < 0) return;
        listRef.current?.scrollToItem(currentIndex, 'center');
    }, [isComponentVisible, isLoading, query, currentIndex]);

    const handleToggle = () => {
        setIsComponentVisible(prev => !prev);
    };

    const handleSelect = useCallback((id: number) => {
        setIsComponentVisible(false);
        if (String(id) === currentId) return;
        history.push(generatePath(routeNames.categoryProfile, { id }));
    }, [currentId, history, setIsComponentVisible]);

    const itemData = useMemo<ItemData>(
        () => ({ options: filtered, currentId, query, onSelect: handleSelect }),
        [filtered, currentId, query, handleSelect],
    );

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className={cssX('SearchBox__loading')} data-testid="blockCategoryProfileSwitcherLoading">
                    Loading…
                </div>
            );
        }
        if (isError) {
            return (
                <div className={cn(cssX('SearchBox__no-results'), 'CategoryProfileSwitcher__error')}>
                    <div>Couldn't load category profiles.</div>
                    <button type="button" onClick={reFetch}>
                        Try again
                    </button>
                </div>
            );
        }
        if (filtered.length === 0) {
            return (
                <div className={cssX('SearchBox__no-results')}>
                    Nothing found.<br />Try searching something else.
                </div>
            );
        }
        return (
            <List
                ref={listRef}
                outerElementType={CustomScrollbarsVirtualList}
                innerElementType={GroupInner}
                height={Math.min(filtered.length, MAX_VISIBLE_ROWS) * ITEM_SIZE}
                width="100%"
                itemCount={filtered.length}
                itemSize={ITEM_SIZE}
                itemData={itemData}
                itemKey={(index, data) => data.options[index].id}
            >
                {Option}
            </List>
        );
    };

    return (
        <div
            className={cn("CategoryProfileSwitcher", className)}
            style={style}
            data-testid={testId}
            ref={ref}
        >
            <button
                type="button"
                ref={triggerRef}
                className="CategoryProfileSwitcher__trigger"
                data-tooltip-id={dropdownId}
                aria-haspopup="true"
                aria-controls={panelId}
                aria-expanded={isComponentVisible}
                onClick={handleToggle}
                data-testid="buttonCategoryProfileSwitcher"
            >
                <span className="CategoryProfileSwitcher__name">{currentName}</span>
                <FiChevronDown
                    className={cn(
                        "CategoryProfileSwitcher__chevron",
                        isComponentVisible && "CategoryProfileSwitcher__chevron--open",
                    )}
                    aria-hidden="true"
                />
            </button>
            <Portal>
                <Dropdown
                    id={dropdownId}
                    isOpened={isComponentVisible}
                    place="bottom-start"
                    offset={8}
                    className="CategoryProfileSwitcher__dropdown"
                    testId="blockCategoryProfileSwitcherDropdown"
                >
                    <div className="CategoryProfileSwitcher__panel" id={panelId} ref={panelRef}>
                        <label className="CategoryProfileSwitcher__search">
                            <FiSearch className="CategoryProfileSwitcher__search-icon" aria-hidden="true" />
                            <span className="CategoryProfileSwitcher__visually-hidden">Search categories</span>
                            <TextInput
                                placeholder="Search Categories"
                                value={search}
                                setValue={setSearch}
                                autoFocus
                            />
                        </label>
                        {renderBody()}
                    </div>
                </Dropdown>
            </Portal>
        </div>
    );
};

export default CategoryProfileSwitcher;
